import type { UserPreferences } from '@/api/session';
import dayjs, { type Dayjs } from './dayjs';

export type DurationFormat = 'FULL' | 'COMPACT' | 'DECIMAL';

/** Dauer formatieren (TT-12): FULL = h:mm:ss, COMPACT = h:mm, DECIMAL = 1,50 h. */
export function formatDuration(seconds: number, format: DurationFormat = 'FULL', locale = 'de'): string {
  const s = Math.max(0, Math.round(seconds));
  if (format === 'DECIMAL') {
    return (s / 3600).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  return format === 'COMPACT' ? `${h}:${mm}` : `${h}:${mm}:${String(sec).padStart(2, '0')}`;
}

/**
 * Dauer-Eingabe parsen: „1:30“, „1:30:15“, „1.5“, „1,5“, „90m“, „1h 30m“, „2h“, „130“ (= 1:30).
 * Liefert Sekunden oder `null` bei ungültiger Eingabe.
 */
export function parseDuration(input: string): number | null {
  const v = input.trim().toLowerCase().replace(/\s+/g, '');
  if (!v) return null;
  let m = /^(\d+):(\d{1,2})(?::(\d{1,2}))?$/.exec(v);
  if (m) return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3] ?? 0);
  m = /^(?:(\d+(?:[.,]\d+)?)h)?(?:(\d+)m(?:in)?)?$/.exec(v);
  if (m && (m[1] || m[2])) {
    return Math.round(Number((m[1] ?? '0').replace(',', '.')) * 3600) + Number(m[2] ?? 0) * 60;
  }
  m = /^(\d+)[.,](\d+)$/.exec(v);
  if (m) return Math.round(Number(`${m[1]}.${m[2]}`) * 3600);
  m = /^(\d+)$/.exec(v);
  if (m) {
    const n = Number(m[1]);
    if (v.length <= 2) return n * 3600; // „8“ → 8 h
    return Math.floor(n / 100) * 3600 + (n % 100) * 60; // „130“ → 1:30
  }
  return null;
}

/** Uhrzeit-Eingabe parsen: „9“, „930“, „9:30“, „0930“, „9.30“, „9:30pm“. Liefert [h, m]. */
export function parseTime(input: string): [number, number] | null {
  const v = input.trim().toLowerCase().replace(/\s+/g, '');
  const m = /^(\d{1,2})(?:[:.]?(\d{2}))?(am|pm)?$/.exec(v);
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2] ?? 0);
  if (m[3] === 'pm' && h < 12) h += 12;
  if (m[3] === 'am' && h === 12) h = 0;
  if (h > 23 || min > 59) return null;
  return [h, min];
}

export function formatMoney(amount: number | null | undefined, currency: string, locale = 'de'): string {
  if (amount == null) return '';
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}

/** Zeitpunkt in der Zeitzone des Benutzers. */
export function inZone(instant: string | Date | Dayjs, prefs: UserPreferences): Dayjs {
  return dayjs(instant).tz(prefs.timezone);
}

export function timeFormat(prefs: UserPreferences): string {
  return prefs.timeFormat === 'H12' ? 'h:mm A' : 'HH:mm';
}

export function formatTime(instant: string, prefs: UserPreferences): string {
  return inZone(instant, prefs).format(timeFormat(prefs));
}

export function formatDate(date: string | Dayjs, prefs: UserPreferences): string {
  return dayjs.isDayjs(date) ? date.format(prefs.dateFormat) : dayjs(date).format(prefs.dateFormat);
}

/** Datum mit Uhrzeit im Format des Benutzers. */
export function dateTimeFormat(prefs: UserPreferences, withSeconds = false): string {
  return `${prefs.dateFormat} ${withSeconds ? timeFormat(prefs).replace('mm', 'mm:ss') : timeFormat(prefs)}`;
}

/** Kurzes Datum ohne Jahr im Format des Benutzers (z. B. 17.10. bzw. 10/17). */
export function shortDate(date: Dayjs, prefs: UserPreferences): string {
  const f = prefs.dateFormat;
  // deutsches Format behält den Punkt am Ende (17.10.), sonst Jahr samt Trennzeichen entfernen
  const pattern = f.endsWith('.YYYY') ? f.slice(0, -4) : f.replace(/[./-]?YYYY[./-]?/, '');
  return date.format(pattern || 'DD.MM.');
}

/** Erster Tag der Woche nach Benutzereinstellung. */
export function startOfWeek(day: Dayjs, prefs: UserPreferences): Dayjs {
  const target = prefs.weekStart === 'SUNDAY' ? 0 : prefs.weekStart === 'SATURDAY' ? 6 : 1;
  const diff = (day.day() - target + 7) % 7;
  return day.subtract(diff, 'day').startOf('day');
}

/** Stabile Farbe aus einem String (z. B. für Avatare). */
export function colorFor(value: string): string {
  const palette = ['blue', 'cyan', 'teal', 'green', 'lime', 'yellow', 'orange', 'red', 'pink', 'grape', 'violet', 'indigo'];
  let hash = 0;
  for (let i = 0; i < value.length; i++) hash = (hash * 31 + value.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length] ?? 'gray';
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
